// AdvancedKpis — sparklines + comparatifs M-1 / Y-1 (Phase 7)
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Minus, TrendingUp, ArrowUpFromLine, ArrowDownToLine, Receipt } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatXAF } from "@/lib/api";

export default function AdvancedKpis({ entityId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    setErr("");
    api.get(`/dashboard/advanced/${entityId}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  }, [entityId]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-44 bg-obsidian-900 rounded-sm animate-pulse" />)}
      </div>
    );
  }
  if (err) return <div className="text-xs text-bad">{err}</div>;
  if (!data) return null;

  const cards = [
    { key: "ca", label: "Chiffre d'affaires", icon: TrendingUp, accent: "citron", block: data.ca },
    { key: "encaissements", label: "Encaissements", icon: ArrowUpFromLine, accent: "ok", block: data.encaissements },
    { key: "decaissements", label: "Décaissements", icon: ArrowDownToLine, accent: "warn", block: data.decaissements },
  ];

  return (
    <section data-testid="advanced-kpis">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="overline text-citron">Analyse 30 jours · Comparatifs</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Tendances avancées</h2>
        </div>
        <span className="text-[10px] text-obsidian-500 font-mono">{data.period?.from} → {data.period?.to}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <AdvCard key={c.key} {...c} index={i} />
        ))}
        <UnpaidCard data={data.invoices} index={3} />
      </div>
    </section>
  );
}

function AdvCard({ label, icon: Icon, accent, block, index }) {
  const v = block?.current_30d ?? 0;
  const mom = block?.delta_pct_mom;
  const yoy = block?.delta_pct_yoy;
  const sparkline = (block?.sparkline || []).map((p) => ({ d: p.d, v: p.v }));
  const colors = { citron: "text-citron", ok: "text-ok", warn: "text-warn", bad: "text-bad" };
  const strokes = { citron: "#D6F32F", ok: "#4ADE80", warn: "#FACC15", bad: "#F87171" };
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className="card p-5 hover:bg-obsidian-850 transition-colors flex flex-col gap-3"
      data-testid={`adv-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">{label}</div>
          <div className={`font-mono text-2xl font-semibold tabular tracking-tight mt-1 ${colors[accent]}`}>
            {formatXAF(v)}
          </div>
        </div>
        <Icon size={14} className="text-obsidian-500" />
      </div>

      <div className="h-12 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sparkline} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={strokes[accent]} stopOpacity={0.4} />
                <stop offset="100%" stopColor={strokes[accent]} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="d" hide />
            <YAxis hide />
            <Tooltip
              cursor={{ stroke: strokes[accent], strokeWidth: 1, strokeDasharray: "2 2" }}
              contentStyle={{ background: "#121416", border: "1px solid #222428", borderRadius: 2, fontSize: 11, padding: "4px 8px" }}
              formatter={(val) => [formatXAF(val), "Jour"]}
              labelFormatter={(l) => l}
            />
            <Area type="monotone" dataKey="v" stroke={strokes[accent]} strokeWidth={1.5} fill={`url(#grad-${label})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center gap-3 text-xs">
        <DeltaPill label="vs 30j" pct={mom} />
        {yoy !== undefined && yoy !== null && <DeltaPill label="vs an dernier" pct={yoy} />}
      </div>
    </motion.div>
  );
}

function DeltaPill({ label, pct }) {
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-obsidian-500 font-mono">
        <Minus size={11} /> {label} —
      </span>
    );
  }
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 font-mono ${up ? "text-ok" : "text-bad"}`}>
      <Icon size={11} /> {up ? "+" : ""}{pct}% <span className="text-obsidian-500">{label}</span>
    </span>
  );
}

function UnpaidCard({ data, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className="card p-5 hover:bg-obsidian-850 transition-colors flex flex-col gap-3"
      data-testid="adv-kpi-impayes"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">Impayés clients</div>
          <div className="font-mono text-2xl font-semibold tabular tracking-tight mt-1 text-warn">
            {formatXAF(data?.unpaid || 0)}
          </div>
        </div>
        <Receipt size={14} className="text-obsidian-500" />
      </div>
      <div className="flex flex-col gap-1.5 text-xs">
        <div className="flex justify-between"><span className="text-obsidian-400">Factures impayées</span><span className="font-mono">{data?.unpaid_count || 0}</span></div>
        <div className="flex justify-between"><span className="text-obsidian-400">Total encaissé</span><span className="font-mono text-ok">{formatXAF(data?.paid_total || 0)}</span></div>
      </div>
    </motion.div>
  );
}
